import React, { useEffect, useState } from 'react';
import { configApi, CreditFeeCoverSpend, FeeConfigItem } from '../../../api/config';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { useToast } from '../../../hooks/useToast';

type Change = Parameters<typeof configApi.updateCreditFeeCover>[0];

/** What the server uses until a limit has been saved. */
const DEFAULT_MAX_CHARGE = 500;

const naira = (n: number) =>
  `₦${Number(n).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Paying back what Safe Haven takes on money received.
 *
 * Its own card because this setting spends PayIQ's money on every credit, so
 * every change says what it will do and asks first, and the card shows what it
 * has cost so far this month.
 */
export const CreditFeeCover: React.FC<{
  fees: FeeConfigItem[];
  canEdit: boolean;
  onSaved: () => Promise<void> | void;
}> = ({ fees, canEdit, onSaved }) => {
  const toast = useToast();
  const feeOf = (key: string) =>
    Number(fees.find((f) => f.feeKey === key)?.amount ?? 0);
  const enabled = feeOf('credit_fee_cover_enabled') >= 1;
  const stampDuty = feeOf('credit_fee_cover_stamp_duty') >= 1;
  const consumer = feeOf('credit_fee_cover_percent');
  const business = feeOf('business_credit_fee_cover_percent');
  const maxCharge = Number(
    fees.find((f) => f.feeKey === 'credit_fee_cover_max_charge')?.amount ?? DEFAULT_MAX_CHARGE,
  );

  const [consumerDraft, setConsumerDraft] = useState(String(consumer));
  const [businessDraft, setBusinessDraft] = useState(String(business));
  const [limitDraft, setLimitDraft] = useState(String(maxCharge));
  const [spend, setSpend] = useState<CreditFeeCoverSpend | null>(null);
  const [pending, setPending] = useState<{ change: Change; message: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [catchingUp, setCatchingUp] = useState(false);

  useEffect(() => {
    setConsumerDraft(String(consumer));
    setBusinessDraft(String(business));
    setLimitDraft(String(maxCharge));
  }, [consumer, business, maxCharge]);

  useEffect(() => {
    configApi
      .getCreditFeeCoverSpend()
      .then(setSpend)
      .catch(() => setSpend(null));
  }, [fees]);

  const valid = (raw: string) => {
    const n = Number(raw);
    return raw.trim() !== '' && Number.isFinite(n) && n >= 0 && n <= 100;
  };
  const percentsDirty =
    Number(consumerDraft) !== consumer || Number(businessDraft) !== business;

  const askToSavePercents = () => {
    if (!valid(consumerDraft) || !valid(businessDraft)) {
      toast.error('A percent must be between 0 and 100.');
      return;
    }
    setPending({
      change: {
        consumerPercent: Number(consumerDraft),
        businessPercent: Number(businessDraft),
      },
      message: `PayIQ will pay back ${Number(consumerDraft)}% of the fee for people and ${Number(businessDraft)}% for businesses, from the settlement account, from the next credit${enabled ? '' : ', once this is turned on'}. Fees already taken are not paid back.`,
    });
  };

  const askToSaveLimit = () => {
    const n = Number(limitDraft);
    if (limitDraft.trim() === '' || !Number.isFinite(n) || n < 0 || n > 100000) {
      toast.error('The limit must be between ₦0 and ₦100,000.');
      return;
    }
    setPending({
      change: { maxCharge: n },
      message: `A single fee above ${naira(n)} will not be paid back automatically, from the next credit. It stays on the credit for someone to look at.`,
    });
  };

  const save = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      await configApi.updateCreditFeeCover(pending.change);
      toast.success('Credit fee cover updated.');
      setPending(null);
      await onSaved();
    } catch (err) {
      toast.error(err, 'Failed to update configuration.');
    } finally {
      setSaving(false);
    }
  };

  const catchUp = async () => {
    setSaving(true);
    try {
      const { covered, amount } = await configApi.catchUpCreditFeeCover();
      toast.success(
        covered ? `Paying back ${covered} fee${covered === 1 ? '' : 's'}, ${naira(amount)} in all.` : 'No fees to pay back.',
      );
      setCatchingUp(false);
      await onSaved();
    } catch (err) {
      toast.error(err, 'Failed to pay back fees.');
    } finally {
      setSaving(false);
    }
  };

  const row: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'minmax(min(100%, 220px), 1fr) auto auto',
    gap: 16,
    padding: '16px 20px',
    alignItems: 'center',
    borderBottom: '1px solid var(--neutral-300)',
  };
  const title: React.CSSProperties = { fontSize: 14, fontWeight: 600, color: 'var(--neutral-800)' };
  const note: React.CSSProperties = { fontSize: 12, color: 'var(--neutral-500)', marginTop: 2 };
  const state = (on: boolean): React.CSSProperties => ({
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'monospace',
    color: on ? 'var(--green-700, #15803d)' : 'var(--neutral-500)',
  });

  const percentInput = (
    label: string,
    value: string,
    onChange: (next: string) => void,
  ) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--neutral-700)' }}>
      {label}
      <input
        aria-label={`${label} percent`}
        type="number"
        min={0}
        max={100}
        step="any"
        disabled={!canEdit}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 80, padding: '6px 8px', border: '1px solid var(--neutral-300)', borderRadius: 6 }}
      />
      %
    </label>
  );

  return (
    <Card padding={0}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--neutral-300)' }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--neutral-800)' }}>Credit Fee Cover</h3>
        <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>
          Safe Haven takes a fee from every credit, so somebody sent ₦1,000 holds ₦995. PayIQ can pay it back from the settlement account, and they see the full amount with no fee and no notice. 100% covers the whole fee.
        </p>
      </div>

      <div style={{ ...row, background: enabled ? 'var(--neutral-100)' : 'var(--neutral-200)' }}>
        <div>
          <div style={title}>Cover fees</div>
          <div style={note}>Off: nothing is paid back, whatever the percents say.</div>
        </div>
        <span style={state(enabled)}>{enabled ? 'ON' : 'OFF'}</span>
        {canEdit ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              setPending({
                change: { enabled: !enabled },
                message: enabled
                  ? 'PayIQ will stop paying back credit fees, from the next credit. People and businesses will see the fee again.'
                  : `PayIQ will start paying back credit fees from the settlement account, from the next credit: ${consumer}% for people and ${business}% for businesses. Fees already taken are not paid back.`,
              })
            }
          >
            {enabled ? 'Turn off' : 'Turn on'}
          </Button>
        ) : null}
      </div>

      <div style={row}>
        <div>
          <div style={title}>Percent covered</div>
          <div style={note}>What is not covered still shows as a fee. 0% covers nothing.</div>
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {percentInput('People', consumerDraft, setConsumerDraft)}
          {percentInput('Businesses', businessDraft, setBusinessDraft)}
        </div>
        {canEdit ? (
          <Button size="sm" disabled={!percentsDirty} onClick={askToSavePercents}>
            Save
          </Button>
        ) : null}
      </div>

      <div style={row}>
        <div>
          <div style={title}>Include stamp duty</div>
          <div style={note}>
            The ₦50 government levy on credits of ₦10,000 and above. Off: it still shows on those credits.
          </div>
        </div>
        <span style={state(stampDuty)}>{stampDuty ? 'ON' : 'OFF'}</span>
        {canEdit ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              setPending({
                change: { coverStampDuty: !stampDuty },
                message: stampDuty
                  ? 'PayIQ will stop paying back stamp duty on credits. It will show on credits of ₦10,000 and above.'
                  : 'PayIQ will also pay back the ₦50 stamp duty on credits of ₦10,000 and above, at the same percent as the fee.',
              })
            }
          >
            {stampDuty ? 'Turn off' : 'Turn on'}
          </Button>
        ) : null}
      </div>

      <div style={row}>
        <div>
          <div style={title}>Largest fee paid back</div>
          <div style={note}>
            The amount paid back is what Safe Haven reports on each credit, so its price changes need nothing here. This only stops an unusually large charge being paid out automatically.
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--neutral-700)' }}>
          ₦
          <input
            aria-label="Largest fee paid back"
            type="number"
            min={0}
            max={100000}
            step="any"
            disabled={!canEdit}
            value={limitDraft}
            onChange={(e) => setLimitDraft(e.target.value)}
            style={{ width: 90, padding: '6px 8px', border: '1px solid var(--neutral-300)', borderRadius: 6 }}
          />
        </label>
        {canEdit ? (
          <Button size="sm" disabled={Number(limitDraft) === maxCharge} onClick={askToSaveLimit}>
            Save
          </Button>
        ) : null}
      </div>

      <div style={{ ...row, gridTemplateColumns: '1fr auto' }}>
        <div>
          <div style={title}>Pay back missed fees</div>
          <div style={note}>Fees still showing on money received in the last 24 hours. Older fees are never paid back.</div>
        </div>
        {canEdit ? (
          <Button size="sm" variant="secondary" disabled={!enabled} onClick={() => setCatchingUp(true)}>
            Pay back
          </Button>
        ) : null}
      </div>

      <div style={{ ...row, borderBottom: 'none', gridTemplateColumns: '1fr auto' }}>
        <div>
          <div style={title}>Cost this month</div>
          <div style={note}>{spend ? `${spend.count} fee${spend.count === 1 ? '' : 's'} covered` : 'Not available'}</div>
          {spend?.pending ? <div style={note}>{spend.pending} waiting on the bank</div> : null}
          {spend?.failed ? (
            <div style={{ ...note, color: 'var(--red-700, #b91c1c)' }}>
              {spend.failed} refused by the bank ({naira(spend.failedAmount)}): move by hand
            </div>
          ) : null}
        </div>
        {spend ? (
          <span style={{ fontSize: 13, fontFamily: 'monospace', color: 'var(--neutral-800)' }}>
            People {naira(spend.consumer)} · Businesses {naira(spend.business)}
          </span>
        ) : null}
      </div>

      <ConfirmDialog
        isOpen={catchingUp}
        title="Pay back missed fees?"
        message="PayIQ will pay back fees still showing on money people received in the last 24 hours, from the settlement account, at the percent set above. Running this again pays nothing twice."
        confirmLabel="Pay back"
        variant="primary"
        loading={saving}
        onConfirm={() => void catchUp()}
        onCancel={() => setCatchingUp(false)}
      />

      <ConfirmDialog
        isOpen={pending !== null}
        title="Change credit fee cover?"
        message={pending?.message ?? ''}
        confirmLabel="Update"
        variant="primary"
        loading={saving}
        onConfirm={() => void save()}
        onCancel={() => setPending(null)}
      />
    </Card>
  );
};
